/*
 * LICENSE PROTECTED SOFTWARE
 * (c) 2026  @cemdemirci0101
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { UserCheck, UserX, ShieldAlert, ArrowLeft, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

const ALLOWED = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'];

const PendingKkApprovalPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [search, setSearch] = useState('');

  const isAllowed = user && ALLOWED.includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await usersAPI.listPendingKkApproval({ limit: 100 });
      setItems(r.data.users || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Liste yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAllowed) load(); else setLoading(false); }, [isAllowed, load]);

  const handleApprove = async (u) => {
    if (!window.confirm(`${u.name} ${u.surname} kullanicisini Kent Konseyi adina ONAYLAMAK istediginizden emin misiniz?`)) return;
    setActing(prev => ({ ...prev, [u.id]: 'approving' }));
    try {
      await usersAPI.approveKk(u.id);
      toast.success('Kullanici onaylandi');
      setItems(prev => prev.filter(x => x.id !== u.id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Onaylama basarisiz');
    } finally {
      setActing(prev => ({ ...prev, [u.id]: null }));
    }
  };

  const handleReject = async (u) => {
    if (!window.confirm(`${u.name} ${u.surname} basvurusunu REDDETMEK istediginizden emin misiniz?`)) return;
    setActing(prev => ({ ...prev, [u.id]: 'rejecting' }));
    try {
      await usersAPI.rejectKk(u.id);
      toast.success('Basvuru reddedildi');
      setItems(prev => prev.filter(x => x.id !== u.id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Red islemi basarisiz');
    } finally {
      setActing(prev => ({ ...prev, [u.id]: null }));
    }
  };

  const filtered = search.trim()
    ? items.filter(u => {
        const q = search.toLowerCase();
        return (
          u.name?.toLowerCase().includes(q) ||
          u.surname?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.tc_kimlik?.includes(q)
        );
      })
    : items;

  if (!isAllowed) {
    return (
      <div className="max-w-2xl mx-auto mt-20 px-6">
        <Card>
          <CardContent className="p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Erisim Yetkiniz Yok</h2>
            <p className="text-sm text-slate-500">
              Bu module yalniz admin, kent konseyi baskani, genel sekreter ve yardimcisi erisebilir.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="pending-kk-approval-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <Link to="/panel/kullanicilar" className="hover:text-blue-600 flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Kullanicilar
            </Link>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <UserCheck className="w-6 h-6 text-amber-600" />
            Onay Bekleyen Kullanicilar
            <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-200">{total}</Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            15 yasin altinda kaydolan ve Kent Konseyi onayi bekleyen kullanicilar
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="kk-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </Button>
        </div>
      </div>

      {/* Arama */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-400 ml-2" />
        <Input
          placeholder="Ad, soyad, e-posta veya TC Kimlik ile ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="kk-search-input"
          className="border-0 focus-visible:ring-0 shadow-none"
        />
      </div>

      {/* Liste */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            {loading ? 'Yukleniyor...' : `${filtered.length} kullanici listeleniyor`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Onay bekleyen kullanici bulunmuyor.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pending-kk-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500 text-xs uppercase tracking-wider">
                    <th className="py-2 pr-3">Ad Soyad</th>
                    <th className="py-2 pr-3">E-posta</th>
                    <th className="py-2 pr-3">Telefon</th>
                    <th className="py-2 pr-3">Yas</th>
                    <th className="py-2 pr-3">Dogum Tarihi</th>
                    <th className="py-2 pr-3">Kayit Tarihi</th>
                    <th className="py-2 pr-3">E-posta Dogrulandi</th>
                    <th className="py-2 pr-3 text-right">Islemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-amber-50/30" data-testid={`pending-kk-row-${u.id}`}>
                      <td className="py-3 pr-3 font-medium text-slate-900">{u.name} {u.surname}</td>
                      <td className="py-3 pr-3 text-slate-600">{u.email}</td>
                      <td className="py-3 pr-3 text-slate-600">{u.phone || '-'}</td>
                      <td className="py-3 pr-3">
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                          {u.age != null ? `${u.age} yas` : '-'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3 text-slate-600">{u.dogum_tarihi || '-'}</td>
                      <td className="py-3 pr-3 text-slate-600">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="py-3 pr-3">
                        {u.is_verified ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">Evet</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-xs">Hayir</Badge>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            onClick={() => handleApprove(u)}
                            disabled={!!acting[u.id]}
                            data-testid={`approve-btn-${u.id}`}
                          >
                            <UserCheck className="w-3.5 h-3.5 mr-1" />
                            {acting[u.id] === 'approving' ? 'Onaylaniyor...' : 'Onayla'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 h-8"
                            onClick={() => handleReject(u)}
                            disabled={!!acting[u.id]}
                            data-testid={`reject-btn-${u.id}`}
                          >
                            <UserX className="w-3.5 h-3.5 mr-1" />
                            {acting[u.id] === 'rejecting' ? 'Redediliyor...' : 'Reddet'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingKkApprovalPage;
